import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useApi } from '../lib/api';
import { date, money } from '../lib/format';
import { useAccounts, useSubsidiaries } from '../lib/lookups';
import { useSession } from '../lib/session';
import type { Bill, Vendor } from '../lib/types';
import { Icons } from '../components/icons';
import { Button, ButtonLink, Card, Empty, Input, PageHeader, Query, Stat, Table, Td, Th, Tr } from '../components/ui';
import { BillStatus, billOverdue } from './Bills';
import { EditPartyModal, NewPartyModal } from './Customers';
import { ArchiveButton, ArchivedBadge, ShowArchived } from '../components/archive';

export function VendorsPage() {
  const navigate = useNavigate();
  const { can } = useSession();
  const subs = useSubsidiaries();
  const accounts = useAccounts();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const q = useApi<Vendor[]>(`/api/vendors${search ? `?search=${encodeURIComponent(search)}` : ''}`);
  const archived = q.data?.filter((v) => v.archivedAt).length ?? 0;
  const open = useApi<Bill[]>('/api/bills?open=true&limit=500');
  const owed = new Map<string, number>();
  for (const b of open.data ?? []) owed.set(b.vendorId, (owed.get(b.vendorId) ?? 0) + b.total - b.amountPaid);

  return (
    <>
      <PageHeader
        title="Vendors"
        description="Who you pay. Balances are approved bills not yet paid; the default expense account fills in new bill lines."
        actions={
          <>
            <ShowArchived count={archived} value={showArchived} onChange={setShowArchived} />
            <div className="relative w-56">
              <span className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-faint">
                <Icons.Search size={15} />
              </span>
              <Input className="pl-8" placeholder="Search vendors" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            {can('ACCOUNTANT') && (
              <Button variant="primary" icon={<Icons.Plus size={15} />} onClick={() => setCreating(true)}>
                New vendor
              </Button>
            )}
          </>
        }
      />
      <Card pad={false}>
        <Query q={q}>
          {(rows) =>
            rows.length === 0 ? (
              <Empty title={search ? 'No vendors match' : 'No vendors yet'}>
                {!search && 'Add the companies you pay, then enter their bills.'}
              </Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Entity</Th>
                    <Th>Default account</Th>
                    <Th>Department</Th>
                    <Th>Terms</Th>
                    <Th right>Owed</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows
                    .filter((v) => showArchived || !v.archivedAt)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((v) => {
                      const a = v.defaultExpenseAccountId ? accounts.byId.get(v.defaultExpenseAccountId) : undefined;
                      return (
                        <Tr key={v.id} onClick={() => navigate(`/ap/vendors/${v.id}`)}>
                          <Td className="font-medium">
                            {v.name}
                            <ArchivedBadge at={v.archivedAt} />
                          </Td>
                          <Td muted>{subs.byId.get(v.subsidiaryId)?.name}</Td>
                          <Td muted>{a ? `${a.code} · ${a.name}` : '—'}</Td>
                          <Td muted>{v.department ?? '—'}</Td>
                          <Td muted>Net {v.paymentTermsDays}</Td>
                          <Td right className={owed.get(v.id) ? 'font-medium' : 'text-faint'}>
                            {money(owed.get(v.id) ?? 0, v.currency)}
                          </Td>
                        </Tr>
                      );
                    })}
                </tbody>
              </Table>
            )
          }
        </Query>
      </Card>
      <NewPartyModal kind="vendor" open={creating} onClose={() => setCreating(false)} onCreated={(id) => navigate(`/ap/vendors/${id}`)} />
    </>
  );
}

export function VendorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useSession();
  const subs = useSubsidiaries();
  const q = useApi<{ vendor: Vendor; bills: Bill[] }>(`/api/vendors/${id}`);
  const [editing, setEditing] = useState(false);
  return (
    <Query q={q}>
      {({ vendor: v, bills }) => {
        const live = bills.filter((b) => !['VOID', 'REJECTED', 'DRAFT'].includes(b.status));
        const open = live.filter((b) => b.status === 'APPROVED' || b.status === 'PARTIALLY_PAID');
        const year = new Date().getFullYear().toString();
        const ytd = live.filter((b) => b.issueDate.startsWith(year)).reduce((s, b) => s + b.total, 0);
        const last12 = live.slice(0, 12);
        const avg = last12.length ? last12.reduce((s, b) => s + b.total, 0) / last12.length : 0;
        return (
          <>
            <PageHeader
              eyebrow={
                <Link to="/ap/vendors" className="hover:text-ink">
                  ← Vendors
                </Link>
              }
              title={
                <>
                  {v.name}
                  <ArchivedBadge at={v.archivedAt} />
                </>
              }
              description={`${v.email ?? 'No remittance email'} · ${subs.byId.get(v.subsidiaryId)?.name} · ${v.currency} · net ${v.paymentTermsDays}`}
              actions={
                can('ACCOUNTANT') && (
                  <>
                    <ArchiveButton path={`/api/vendors/${v.id}`} archived={!!v.archivedAt} noun="Vendor" />
                    <Button onClick={() => setEditing(true)}>Edit</Button>
                    <ButtonLink to={`/ap/bills/new?vendorId=${v.id}`} variant="primary" icon={<Icons.Plus size={15} />}>
                      New bill
                    </ButtonLink>
                  </>
                )
              }
            />
            {editing && <EditPartyModal kind="vendor" party={v} onClose={() => setEditing(false)} />}
            <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Owed" value={money(open.reduce((s, b) => s + b.total - b.amountPaid, 0), v.currency)} sub={`${open.length} open bills`} />
              <Stat label="Overdue" value={money(open.filter(billOverdue).reduce((s, b) => s + b.total - b.amountPaid, 0), v.currency)} tone={open.some(billOverdue) ? 'bad' : 'good'} />
              <Stat label={`Spend ${year}`} value={money(ytd, v.currency, { compact: true })} />
              <Stat label="Average bill" value={money(Math.round(avg), v.currency)} sub="Last 12 bills" />
            </div>
            <Card title="Bills" pad={false}>
              {bills.length ? (
                <Table>
                  <thead>
                    <tr>
                      <Th>Number</Th>
                      <Th>Issued</Th>
                      <Th>Due</Th>
                      <Th right>Total</Th>
                      <Th>Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {bills.map((b) => (
                      <Tr key={b.id} onClick={() => navigate(`/ap/bills/${b.id}`)}>
                        <Td className="font-medium">{b.number}</Td>
                        <Td className="num">{date(b.issueDate)}</Td>
                        <Td className="num">{date(b.dueDate)}</Td>
                        <Td right>{money(b.total, b.currency)}</Td>
                        <Td>
                          <BillStatus b={b} />
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Empty title="No bills yet" />
              )}
            </Card>
          </>
        );
      }}
    </Query>
  );
}
